import React from 'react';
import axios from 'axios';
import { useCallbackRef } from '@10d/tend-ui-hooks';
import { useClient } from '../../context.js';
import { CacheManager } from '../../CacheManager.js';

const usePaginationApi = (api) => {
    const client = useClient();
    const [loading, setLoading] = React.useState(false);
    const [info, setInfo] = React.useState(null);
    const [data, setData] = React.useState(null);
    const [error, setError] = React.useState(null);
    const isMoreLoading = React.useRef(false);
    const request = useCallbackRef((payload) => {
        setLoading(true);
        if (typeof api === 'string') {
            return client
                .get(api, { params: payload === null || payload === void 0 ? void 0 : payload.params })
                .then(response => {
                setLoading(false);
                setInfo(response);
                setData(response.results);
                return response;
            })
                .catch(e => {
                if (axios.isCancel(e))
                    throw e;
                setLoading(false);
                setError(e);
                throw e;
            });
        }
        if (typeof api === 'function') {
            return api(payload)
                .then(response => {
                setInfo(response);
                setData(response.results);
                return response;
            })
                .catch(e => {
                setError(e);
                throw e;
            })
                .finally(() => {
                setLoading(false);
            });
        }
        if ('url' in api) {
            return client
                .get(api.url, {
                params: payload === null || payload === void 0 ? void 0 : payload.params,
                cancellable: api.cancellable,
            })
                .then(response => {
                setLoading(false);
                setInfo(response);
                setData(response.results);
                return response;
            })
                .catch(e => {
                if (axios.isCancel(e))
                    throw e;
                setLoading(false);
                setError(e);
                throw e;
            });
        }
        return api
            .fn(payload)
            .then(response => {
            setInfo(response);
            setData(response.results);
            return response;
        })
            .catch(e => {
            setError(e);
            throw e;
        })
            .finally(() => {
            setLoading(false);
        });
    });
    const cacheRequest = useCallbackRef((payload, options) => {
        var _a, _b;
        if (typeof api === 'string' || typeof api === 'function' || !api.cache) {
            return request(payload);
        }
        const force = (_a = options === null || options === void 0 ? void 0 : options.force) !== null && _a !== void 0 ? _a : false;
        const key = api.cache.key;
        const manager = new CacheManager({
            cacheTime: api === null || api === void 0 ? void 0 : api.cache.cacheTime,
            staleTime: (_b = api === null || api === void 0 ? void 0 : api.cache) === null || _b === void 0 ? void 0 : _b.staleTime,
        });
        if (manager.isExists(key) && !force) {
            const cached = manager.get(key);
            if (manager.isFresh(key)) {
                // if staled, refreshing data for next request
                if (manager.isStale(key)) {
                    request(payload).then(response => {
                        manager.set(key, response);
                        return response;
                    });
                }
                setLoading(false);
                setInfo(cached.data);
                setData(cached.data.results);
                return Promise.resolve(cached.data);
            }
        }
        return request(payload).then(response => {
            if (!force) {
                manager.set(key, response);
            }
            return response;
        });
    });
    const next = useCallbackRef(() => {
        if (!(info === null || info === void 0 ? void 0 : info.next) || isMoreLoading.current)
            return;
        isMoreLoading.current = true;
        client.get(info === null || info === void 0 ? void 0 : info.next).then(response => {
            isMoreLoading.current = false;
            setInfo(response);
            setData(previous => [...(previous || []), ...response.results]);
        });
    });
    return {
        data,
        loading,
        error,
        request: cacheRequest,
        next,
    };
};

export { usePaginationApi };
//# sourceMappingURL=usePaginationApi.js.map
